import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { Router } from '@angular/router';
import { secureStorage } from '../../shared/services/secure-storage.service';
import { CookieService } from '../../shared/services/cookie.service';
import { RootService } from '../../shared/services/root.service';
import { TokenService } from '../../shared/services/token.service';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CartService } from '../../shared/services/cart.service';
import { BuyerVerificationService } from '../../shared/services/buyer-verification.service';
import { GstinItem } from '../../shared/models/buyer-verification.models';

export type BusinessStepResult =
  | { skipped: true }
  | { gstins: GstinItem[]; businessFullName: string };

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit, OnDestroy {
  /** When true, show only the business-details step (checkout resume). */
  @Input() businessOnly = false;
  @Input() checkoutMode = false;

  @Output() registrationSuccess = new EventEmitter<void>();
  @Output() businessStepComplete = new EventEmitter<BusinessStepResult>();
  @Output() closeModal = new EventEmitter<void>();

  registerStep: 'personal' | 'business' = 'personal';

  personalDetails: PersonalDetails = {
    firstName: '',
    lastName: '',
    email: '',
  };

  userTypes: UserType[] = USER_TYPES;
  selectedUserType: UserType | null = null;
  loading = false;
  errorMessage = '';
  private sub: Subscription = new Subscription();

  // Business details
  panNumber = '';
  businessFullName = '';
  panTouched = false;
  fullNameTouched = false;
  businessLoadingLabel = 'Continue';

  showFirstNameSuggestions = false;
  firstNameSuggestions: string[] = [];
  filteredFirstNameSuggestions: string[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
    private localstorage: secureStorage,
    private storageService: RootService,
    private ssrcookieService: CookieService,
    private tokenService: TokenService,
    private cartService: CartService,
    private buyerVerificationService: BuyerVerificationService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) { }

  ngOnInit(): void {
    if (this.businessOnly) {
      this.registerStep = 'business';
      this.businessFullName = this.resolveFullName();
    }
  }

  isFormValid(): boolean {
    return (
      !!this.personalDetails.firstName?.trim() &&
      !!this.personalDetails.lastName?.trim() &&
      this.isNameValid(this.personalDetails.firstName) &&
      this.isNameValid(this.personalDetails.lastName) &&
      this.isEmailValid(this.personalDetails.email) &&
      !!this.selectedUserType
    );
  }

  isBusinessValid(): boolean {
    return (
      this.isPanValid(this.panNumber) && !!this.businessFullName?.trim()
    );
  }

  isPanValid(pan: string): boolean {
    return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((pan || '').toUpperCase());
  }

  onSubmit(): void {
    if (this.registerStep === 'business') {
      this.onBusinessSubmit();
      return;
    }

    this.firstNameTouched = true;
    this.lastNameTouched = true;
    this.emailTouched = true;
    this.userTypeTouched = true;

    if (this.isFormValid() && !this.loading) {
      this.loading = true;
      this.errorMessage = '';

      const phoneNumber = localStorage.getItem('registrationPhoneNumber');
      const otpNumber = localStorage.getItem('registrationOtp');

      if (!phoneNumber || !otpNumber) {
        this.errorMessage = 'Missing phone number or OTP. Please try again.';
        this.loading = false;
        return;
      }

      const registrationData = {
        phone_number: phoneNumber,
        first_name: this.personalDetails.firstName.trim(),
        last_name: this.personalDetails.lastName.trim(),
        email_id: this.personalDetails.email?.trim() || '',
        user_type: this.selectedUserType?.label || '',
      };

      this.sub.add(
        this.authService.register(registrationData).subscribe({
          next: (response) => {
            this.loading = false;

            const token = response?.data?.token;
            if (token?.access_token && response?.data?.user_code) {
              this.tokenService.storeAuthData({
                access_token: token.access_token,
                refresh_token: token.refresh_token,
                user_code: response.data.user_code,
                expires_in: token.expires_in,
              });
            }

            const userData = {
              ...(response?.data ?? {}),
              first_name: this.personalDetails.firstName.trim(),
              last_name: this.personalDetails.lastName.trim(),
              email_id: this.personalDetails.email?.trim() || '',
              user_type: this.selectedUserType?.label || '',
              phone_number: phoneNumber,
            };

            this.ssrcookieService.setItem('eol_user', JSON.stringify(userData));
            this.localstorage.setItem('eol_user', JSON.stringify(userData));

            localStorage.removeItem('registrationOtp');

            this.authService.login(userData);
            setTimeout(() => this.authService.refreshAuthState(), 100);

            this.syncLocalStorageCart();
            this.businessFullName = `${this.personalDetails.firstName} ${this.personalDetails.lastName}`.trim();
            this.registerStep = 'business';
            this.errorMessage = '';
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.ngZone.run(() => {
              this.loading = false;
              this.errorMessage =
                this.extractErrorMessage(error) ||
                'Registration failed. Please try again.';
              this.cdr.detectChanges();
            });
          },
        }),
      );
    }
  }

  onBusinessSubmit(): void {
    this.fullNameTouched = true;
    this.panTouched = true;
    this.errorMessage = '';

    if (!this.isBusinessValid() || this.loading) return;

    this.loading = true;
    this.businessLoadingLabel = 'Looking up...';
    this.cdr.detectChanges();

    this.sub.add(
      this.buyerVerificationService
        .verifyPan({
          pan_number: this.panNumber.toUpperCase(),
          full_name: this.businessFullName.trim(),
          dob: '1990-01-01',
        })
        .subscribe({
          next: () => {
            this.ngZone.run(() => {
              this.businessLoadingLabel = 'Fetching GSTINs...';
              this.cdr.detectChanges();
            });
            this.sub.add(
              this.buyerVerificationService.fetchGstins().subscribe({
                next: (data) => {
                    this.ngZone.run(() => {
                      this.loading = false;
                      this.businessLoadingLabel = 'Continue';
                      this.buyerVerificationService.clearSkipped();
                      this.businessStepComplete.emit({
                        gstins: data.gstins ?? [],
                        businessFullName: this.businessFullName.trim(),
                      });
                      this.cdr.detectChanges();
                    });
                  },
                      error: (error: any) => {
                        this.ngZone.run(() => {
                          this.loading = false;
                          this.businessLoadingLabel = 'Continue';
                          this.errorMessage =
                            this.extractErrorMessage(error) ||
                            'Failed to fetch GSTIN details. Please try again.';
                          this.cdr.detectChanges();
                        });
                      },
              }),
            );
          },
            error: (error: any) => {
              this.ngZone.run(() => {
                this.loading = false;
                this.businessLoadingLabel = 'Continue';
                this.errorMessage =
                  this.extractErrorMessage(error) ||
                  'PAN verification failed. Please check the PAN number or full name and try again.';
                this.cdr.detectChanges();
              });
            },
        }),
    );
  }

  onSkipBusiness(): void {
    this.buyerVerificationService.markSkipped();
    this.businessStepComplete.emit({ skipped: true });
  }

  onPanInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.panNumber = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    input.value = this.panNumber;
    if (this.errorMessage) {
      this.errorMessage = '';
      this.cdr.detectChanges();
    }
  }

  private extractErrorMessage(error: any): string {
    if (!error) return '';
    if (typeof error === 'string') return error;

    let errBody = error?.error;
    if (typeof errBody === 'string') {
      try {
        errBody = JSON.parse(errBody);
      } catch {
        // keep string
      }
    }

    const apiMessage =
      errBody?.data?.msg ||
      errBody?.data?.message ||
      errBody?.message ||
      errBody?.msg ||
      error?.data?.msg ||
      error?.data?.message ||
      error?.msg;

    if (apiMessage && typeof apiMessage === 'string') {
      return apiMessage;
    }

    if (
      error?.message &&
      typeof error.message === 'string' &&
      !error.message.startsWith('Http failure response')
    ) {
      return error.message;
    }

    return '';
  }

  onPanBlur(): void {
    this.panTouched = true;
  }

  onFullNameBlur(): void {
    this.fullNameTouched = true;
  }

  getFullNameError(): string {
    if (!this.fullNameTouched) return '';
    if (!this.businessFullName?.trim()) return 'Full name is required';
    return '';
  }

  getPanError(): string {
    if (!this.panTouched) return '';
    if (!this.panNumber?.trim()) return 'PAN number is required';
    if (!this.isPanValid(this.panNumber)) {
      return 'Enter a valid PAN (e.g. ABCDE1234F)';
    }
    return '';
  }

  private resolveFullName(): string {
    const user = this.authService.userData;
    const first = user?.first_name?.trim() ?? '';
    const last = user?.last_name?.trim() ?? '';
    return `${first} ${last}`.trim();
  }

  private syncLocalStorageCart(): void {
    if (this.cartService?.hasLocalStorageItems()) {
      this.cartService.syncLocalStorageToServer().subscribe({
        error: (err) =>
          console.error('Error syncing local storage cart items:', err),
      });
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onClose(): void {
    if (this.registerStep === 'business') {
      this.closeModal.emit();
      return;
    }
    this.registrationSuccess.emit();
    this.router.navigate(['/home']);
  }

  validateNameInput(event: any, field: 'firstName' | 'lastName'): void {
    const input = event.target;
    const value = input.value;
    const filteredValue = value.replace(/[^a-zA-Z]/g, '');

    if (field === 'firstName') {
      this.personalDetails.firstName = filteredValue;
    } else {
      this.personalDetails.lastName = filteredValue;
    }
    input.value = filteredValue;
  }

  isNameValid(name: string): boolean {
    return /^[a-zA-Z]+$/.test(name);
  }

  isEmailValid(email: string | undefined): boolean {
    if (!email || email.trim() === '') return true;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  getFirstNameError(): string {
    if (!this.firstNameTouched) return '';
    if (!this.personalDetails.firstName) return 'First name is required';
    if (!this.isNameValid(this.personalDetails.firstName)) {
      return 'First name should contain only letters';
    }
    return '';
  }

  getLastNameError(): string {
    if (!this.lastNameTouched) return '';
    if (!this.personalDetails.lastName) return 'Last name is required';
    if (!this.isNameValid(this.personalDetails.lastName)) {
      return 'Last name should contain only letters';
    }
    return '';
  }

  getEmailError(): string {
    if (!this.emailTouched) return '';
    if (
      this.personalDetails.email &&
      !this.isEmailValid(this.personalDetails.email)
    ) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  getUserTypeError(): string {
    if (!this.userTypeTouched) return '';
    if (!this.selectedUserType) return 'Please select a user type';
    return '';
  }

  firstNameTouched = false;
  lastNameTouched = false;
  emailTouched = false;
  userTypeTouched = false;

  onFirstNameBlur(): void {
    this.firstNameTouched = true;
  }

  onLastNameBlur(): void {
    this.lastNameTouched = true;
  }

  onEmailBlur(): void {
    this.emailTouched = true;
  }

  onUserTypeSelect(type: UserType): void {
    this.userTypes.forEach((t) => (t.selected = false));
    type.selected = true;
    this.selectedUserType = type;
    this.userTypeTouched = true;
  }
}

export interface UserType {
  label: string;
  selected: boolean;
}

export const USER_TYPES: UserType[] = [
  { label: 'Retailer', selected: false },
  { label: 'End Product Manufacturer', selected: false },
  { label: 'Scrap Recycler', selected: false },
  { label: 'Scrap Trader', selected: false },
  { label: 'Refurbisher', selected: false },
];

export type UserTypeLabel =
  | 'Retailer'
  | 'End Product Manufacturer'
  | 'Scrap Recycler'
  | 'Scrap Trader'
  | 'Refurbisher';

export interface PersonalDetails {
  firstName: string;
  lastName: string;
  email?: string;
}

